import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  FileText, 
  Shield, 
  CheckCircle,
  Smartphone,
  CreditCard,
  ArrowLeft,
  ArrowRight,
  User,
  MapPin,
  Sparkles,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const DriverRequirements = () => {
  const basicRequirements = [
    {
      icon: User,
      title: "Be 21+",
      description: "Gotta be at least 21 to drive with us (25 for premium rides). Sorry, not sorry—insurance rules."
    },
    {
      icon: FileText,
      title: "Valid License",
      description: "Active driver's license for at least a year with a clean record. No road rage history, please."
    },
    {
      icon: Shield,
      title: "Background Check",
      description: "We run a real check—criminal and driving history. Keeps everyone safe, including you."
    },
    {
      icon: CreditCard,
      title: "Insurance",
      description: "Valid auto insurance that meets your state's requirements. Non-negotiable."
    },
    {
      icon: Smartphone,
      title: "Decent Phone",
      description: "iPhone (iOS 13+) or Android (8.0+) with a data plan. The app needs to actually work."
    },
    {
      icon: MapPin,
      title: "Know Your Area",
      description: "Be familiar with your city and comfortable using navigation apps. GPS is your friend."
    }
  ];

  const vehicleRequirements = [
    "Car from 2010 or newer (15 years max)",
    "4 doors—no sports cars, sorry",
    "Good condition, passes inspection",
    "Registered in your name",
    "No taxi markings or commercial branding",
    "No salvage or rebuilt titles",
    "AC and heat that actually work",
    "Clean inside and out (basic hygiene applies)"
  ];

  const documents = [
    { name: "Driver's License", description: "Front and back, current and not expired" },
    { name: "Vehicle Registration", description: "Current registration with your name on it" },
    { name: "Proof of Insurance", description: "Policy that meets state minimums" },
    { name: "Vehicle Inspection", description: "Passed within the last year" },
    { name: "Profile Photo", description: "Clear, recent pic of your face (smile optional)" }
  ];

  const steps = [
    { step: 1, title: "Sign Up", description: "Takes like 5 minutes. Basic info stuff." },
    { step: 2, title: "Upload Docs", description: "Snap pics of your documents in the app" },
    { step: 3, title: "Background Check", description: "We handle this—usually a few days" },
    { step: 4, title: "Car Inspection", description: "Quick inspection at an approved spot" },
    { step: 5, title: "Start Earning", description: "Go online and get your first ride!" }
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-400 hover:text-white gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-['Barlow_Condensed'] font-black text-white uppercase mb-6">
            What You Need to Drive
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            No BS—here's exactly what it takes to start earning with Reddi. 
            Meet the requirements and you could be driving by next week.
          </p>
          <Link to="/register?type=driver">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg gap-2">
              Apply to Drive
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Basic Requirements */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4 text-center">
            The Basics
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Standard stuff that keeps everyone safe and legal.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {basicRequirements.map((req, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 p-6 hover:border-red-500/50 transition-colors"
              >
                <req.icon className="w-8 h-8 text-red-500 mb-4" />
                <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">
                  {req.title}
                </h3>
                <p className="text-slate-400 text-sm">{req.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Requirements */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <Car className="w-10 h-10 text-red-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Your Ride
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {vehicleRequirements.map((req, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-slate-800/30 border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-300">{req}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <FileText className="w-10 h-10 text-red-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              Documents We Need
            </h2>
          </div>
          <div className="space-y-4">
            {documents.map((doc, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-6 bg-slate-800/50 border border-slate-700"
              >
                <div>
                  <h3 className="text-white font-semibold">{doc.name}</h3>
                  <p className="text-slate-400 text-sm">{doc.description}</p>
                </div>
                <CheckCircle className="w-6 h-6 text-slate-600" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-12 justify-center">
            <Sparkles className="w-8 h-8 text-amber-500" />
            <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase">
              How It Works
            </h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-slate-700" />
            <div className="grid md:grid-cols-5 gap-8">
              {steps.map((item) => (
                <div key={item.step} className="text-center relative">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                    <span className="text-2xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Time Estimate */}
      <section className="py-12 px-6 md:px-12 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-4 p-6 bg-green-600/10 border border-green-600/30">
            <Clock className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-white font-semibold">Most drivers get approved in 3-7 days</p>
              <p className="text-slate-400 text-sm">Background checks take the longest—everything else is pretty quick.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">
            Ready to Start?
          </h2>
          <p className="text-slate-400 mb-8">
            Got a car, a license, and some free time? Turn that into cash.
          </p>
          <Link to="/register?type=driver">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-lg">
              Apply Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          © 2025 REDDI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default DriverRequirements;
